import { range } from 'lodash-es';
import { BaseCalendarModel } from '../../../../core/models/calendar/base-calendar.model';
import { DayOrWeekCalendarModel } from '../../../../core/models/calendar/day-or-week-calendar.model';
import { SidebarService } from '../../../../core/services/ui/sidebar.service';
import {
  CalendarEvents,
  CalendarZoomFactors,
  DayComposite,
  calendarSlotHeightMap,
} from '../../calendar-types';
import { TimeBlockCrudService } from '../../components/time-block/crud/time-block-crud.service';
import { CalendarServiceHelper } from '../../services/calendar-service-helper';
import { CalendarViewHandlerService } from '../../services/calendar-view-handler.service';
import { CalendarService } from '../../services/calendar.service';
import { TimeCoordinateMappingService } from '../../time-mapping/time-coordinate-mapping.service';
import { ViewHandler } from './view-handler';

export abstract class DayOrWeekViewHandler extends ViewHandler {
  public days: DayComposite[] = []; // Days for calendar body
  public slotHeight = -1;
  public zoomFactorCSSClass = CalendarZoomFactors[CalendarZoomFactors.ZoomZero];
  private hours = new Array<string>();

  protected constructor(
    calendarService: CalendarService,
    timeBlockCrudService: TimeBlockCrudService,
    timeCoordinateMappingService: TimeCoordinateMappingService,
    sidebarService: SidebarService,
    calendarViewHandlerService: CalendarViewHandlerService,
  ) {
    super(
      calendarService,
      timeBlockCrudService,
      timeCoordinateMappingService,
      sidebarService,
      calendarViewHandlerService,
    );

    // Set slot height depending on zoom factor
    const zoomFactor =
      (calendarService.model as DayOrWeekCalendarModel).calendarProperties?.zoomFactor ??
      CalendarZoomFactors.ZoomZero;
    this.slotHeight = calendarSlotHeightMap.get(zoomFactor);
    this.zoomFactorCSSClass = CalendarZoomFactors[zoomFactor];
  }

  get hourLabels(): string[] {
    return this.hours;
  }

  protected initEvents(): void {
    const targetEvents = [
      CalendarEvents.ChangedStartEndDates,
      CalendarEvents.InitializedCalendarView,
    ];
    const callback = (calendarModel: BaseCalendarModel): void => {
      if (!(calendarModel instanceof DayOrWeekCalendarModel)) {
        return;
      }
      // Purge store
      this.tbCrudService.purgeTimeBlockStore();
      this.days = calendarModel.calendarProperties.days;

      this.tbCrudService.reloadTimeBlocks$.next(true);
    };

    this.sendTimeBlocksToView();

    // Execute callback when calendar model was updated
    this.subs.sink = CalendarServiceHelper.calendarModelUpdated(
      this.calService,
      callback,
      targetEvents,
    );
  }

  protected mapHoursOfDay(): void {
    const hoursOfDay = (this.calService.model as DayOrWeekCalendarModel).calendarProperties
      .hoursOfDay;
    if (hoursOfDay === null || hoursOfDay === undefined) {
      throw new Error('No hours of day set.');
    }
    this.hours = range(0, hoursOfDay).map((hour) => {
      return `${hour.toString().padStart(2, '0')}:00`;
    });
  }

  protected updateAndRenderFixIntervalContainer(): void {
    const model = this.calService.model as DayOrWeekCalendarModel;
    const fixIntervalGeometryData = model.fixIntervalCalendarModel.geometryData;
    const fixIntervalHTMLContainer = fixIntervalGeometryData.calendarBodyElementRef.nativeElement;

    const bcr = fixIntervalHTMLContainer.getBoundingClientRect();
    fixIntervalGeometryData.calendarBodyElementRef =
      model.fixIntervalCalendarModel.geometryData.calendarBodyElementRef;
    fixIntervalGeometryData.calendarBodyOffsetLeft = Math.abs(bcr.left);
    fixIntervalGeometryData.calendarBodyOffsetTop = bcr.top;
    fixIntervalGeometryData.calendarBodyWidth = bcr.width;
  }
}
